// 破産確率カーブ（§5.4 / §9）。Chart.js line + フル/ハーフ/クォーター/現在B の縦マーカー。
import {
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type Plugin,
} from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, Filler, Tooltip);

const COL = {
  blood: '#cd4d3e',
  gold: '#e0a53b',
  cyan: '#74b8c6',
  ink: '#ece6d9',
  line: '#2b3043',
  faint: '#6f6d66',
};

export interface RuinMarkers {
  full: number;
  half: number;
  quarter: number;
  current?: number | null;
}

interface WithMarkers {
  _markers?: RuinMarkers;
  _unit?: string;
}

// 縦マーカー描画プラグイン。
const markerPlugin: Plugin<'line'> = {
  id: 'ruinMarkers',
  afterDatasetsDraw(chart) {
    const m = (chart as unknown as WithMarkers)._markers;
    if (!m) return;
    const { ctx, chartArea, scales } = chart;
    const xs = scales.x;
    if (!xs) return;
    const draw = (val: number | null | undefined, color: string, dashed: boolean, label: string) => {
      if (val == null || !Number.isFinite(val)) return;
      const x = xs.getPixelForValue(val);
      if (x < chartArea.left - 1 || x > chartArea.right + 1) return;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash(dashed ? [4, 3] : []);
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.strokeStyle = color;
      ctx.lineWidth = dashed ? 1 : 1.75;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.font = '10px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(label, x, chartArea.top - 4);
      ctx.restore();
    };
    draw(m.full, COL.blood, true, 'フル');
    draw(m.half, COL.gold, true, 'ハーフ');
    draw(m.quarter, COL.cyan, true, 'クォーター');
    draw(m.current, COL.ink, false, '現在');
  },
};

export class RuinChart {
  private chart: Chart<'line', { x: number; y: number }[]> | null = null;
  constructor(private readonly canvas: HTMLCanvasElement) {}

  update(curve: Array<{ B: number; p: number }>, markers: RuinMarkers, unit: string): void {
    const data = curve.map((pt) => ({ x: pt.B, y: pt.p * 100 }));
    if (!this.chart) {
      this.chart = new Chart(this.canvas, {
        type: 'line',
        data: {
          datasets: [
            {
              data,
              parsing: false,
              borderColor: COL.gold,
              borderWidth: 2,
              fill: true,
              backgroundColor: (c) => {
                const { ctx, chartArea } = c.chart;
                if (!chartArea) return 'rgba(224,165,59,0.08)';
                const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                g.addColorStop(0, 'rgba(205,77,62,0.22)');
                g.addColorStop(1, 'rgba(224,165,59,0.02)');
                return g;
              },
              tension: 0.25,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: COL.gold,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 16 } },
          scales: {
            x: {
              type: 'linear',
              title: { display: true, text: `軍資金 (${unit})`, color: COL.faint, font: { size: 10 } },
              ticks: { color: COL.faint, font: { family: 'JetBrains Mono', size: 10 }, maxTicksLimit: 6 },
              grid: { color: COL.line },
              border: { color: COL.line },
            },
            y: {
              min: 0,
              max: 100,
              title: { display: true, text: '破産確率 %', color: COL.faint, font: { size: 10 } },
              ticks: { color: COL.faint, font: { family: 'JetBrains Mono', size: 10 }, maxTicksLimit: 6 },
              grid: { color: COL.line },
              border: { color: COL.line },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                title: (items) => `軍資金 ${Math.round(items[0]?.parsed.x ?? 0).toLocaleString()} ${unit}`,
                label: (item) => `破産確率 ${(item.parsed.y ?? 0).toFixed(1)}%`,
              },
            },
          },
        },
        plugins: [markerPlugin],
      });
    } else {
      this.chart.data.datasets[0]!.data = data;
      const sx = this.chart.options.scales?.x;
      if (sx && 'title' in sx && sx.title) sx.title.text = `軍資金 (${unit})`;
    }
    const tagged = this.chart as unknown as WithMarkers;
    tagged._markers = markers;
    tagged._unit = unit;
    this.chart.update('none');
  }

  destroy(): void {
    this.chart?.destroy();
    this.chart = null;
  }
}
